import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import CTAPanel from "@/components/base/CTAPanel";
import { insights, contactEmail } from "@/mocks/insights";

function SectionList({
  title,
  icon,
  items,
  accent = false,
}: {
  title: string;
  icon: string;
  items: string[];
  accent?: boolean;
}) {
  return (
    <div className="card-base p-6 md:p-8">
      <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-900 mb-5">
        <span
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            accent ? "bg-accent-100 text-accent-700" : "bg-primary-50 text-primary-600"
          }`}
        >
          <i className={`${icon} text-lg`} aria-hidden="true"></i>
        </span>
        {title}
      </h2>
      <ul className="space-y-3.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm text-foreground-700 leading-relaxed">
            <i
              className={`${accent ? "ri-close-line text-accent-600" : "ri-check-line text-primary-600"} mt-0.5 shrink-0`}
              aria-hidden="true"
            ></i>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Checklist({ items }: { items: string[] }) {
  return (
    <div className="card-base p-6 md:p-8">
      <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-900 mb-5">
        <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center shrink-0">
          <i className="ri-list-check-3 text-lg" aria-hidden="true"></i>
        </span>
        Action Checklist
      </h2>
      <ol className="space-y-3">
        {items.map((item, index) => (
          <li key={item} className="flex items-start gap-3 text-sm text-foreground-700 leading-relaxed">
            <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              {index + 1}
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function InsightArticle() {
  const { slug } = useParams<{ slug: string }>();
  const insight = insights.find((item) => item.slug === slug);

  if (!insight) {
    return (
      <Layout>
        <PageHero title="Insight Not Found" subtitle="Thought Leadership" size="sm" />
        <section className="section-padding bg-background-50">
          <div className="container-page text-center">
            <p className="text-sm text-foreground-600 mb-6">
              We couldn't find that insight. Please browse the full library.
            </p>
            <Link to="/resources/insights" className="btn-primary">
              Browse All Insights
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        title={insight.title}
        subtitle="Insight"
        description={insight.summary}
        size="md"
      />

      <section className="section-padding bg-background-50">
        <div className="container-page">
          <div className="max-w-4xl mx-auto">
            {/* Meta bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
              <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="Breadcrumb">
                <Link to="/resources/insights" className="hover:text-primary-600 transition-colors">
                  Insights
                </Link>
                <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                <span className="text-foreground-900 font-medium">{insight.category}</span>
              </nav>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800">
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                  {insight.category}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {insight.readingTime}
                </span>
              </div>
            </div>

            {/* Business challenge */}
            <div className="card-base p-6 md:p-8 mb-6">
              <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-900 mb-4">
                <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                  <i className="ri-question-line text-lg" aria-hidden="true"></i>
                </span>
                Business Challenge
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {insight.businessChallenge}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <SectionList
                title="Key Considerations"
                icon="ri-lightbulb-line"
                items={insight.keyConsiderations}
              />
              <SectionList
                title="Practical Recommendations"
                icon="ri-tools-line"
                items={insight.practicalRecommendations}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <SectionList
                title="Common Risks"
                icon="ri-alert-line"
                items={insight.commonRisks}
                accent
              />
              <Checklist items={insight.actionChecklist} />
            </div>

            {/* Related capabilities */}
            <div className="card-base p-6 md:p-8 mb-14">
              <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-900 mb-5">
                <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                  <i className="ri-stack-line text-lg" aria-hidden="true"></i>
                </span>
                Related Mitaja Capabilities
              </h2>
              <div className="flex flex-wrap gap-2.5">
                {insight.relatedCapabilities.map((cap) => (
                  <Link
                    key={cap.href}
                    to={cap.href}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-white border border-primary-200 text-primary-700 hover:bg-primary-50 transition-colors"
                  >
                    {cap.label}
                    <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
                  </Link>
                ))}
              </div>
            </div>

            <CTAPanel
              title="Discuss How Mitaja Can Support Your Delivery Team"
              description="Reach out to our government sales team to explore how our capabilities and technical delivery teams align with your program requirements."
            >
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={`mailto:${contactEmail}`}
                  className="btn-primary"
                >
                  <i className="ri-mail-line mr-2" aria-hidden="true"></i>
                  {contactEmail}
                </a>
                <Link to="/contact" className="btn-secondary border-primary-500">
                  Contact Us
                </Link>
              </div>
            </CTAPanel>
          </div>
        </div>
      </section>
    </Layout>
  );
}